import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { saveUiPreference } from '../api/chatAI';

/**
 * Single-section-open accordion wrapper for the CaseProfile left panel.
 * Persists the open section via PUT /api/chat-ai/ui-preferences
 * (key: left_panel_open_section). A stale reload from the server after
 * persistence is intentional — the source of truth is the server so the
 * same user sees the same open section on a different device.
 *
 * Styling matches CaseProfile's original SectionHeader so the transition
 * is visually seamless: tiny uppercase gray labels, thin top border, small
 * gray chevron, optional badge pill for counts.
 *
 * Props:
 *   sections:      [{ id, label, badge?, badgeColor?, warning?, render: () => ReactNode }]
 *   defaultOpenId: string (from server-persisted ui_preferences.left_panel_open_section)
 *                  or null for "all closed". Pass null to start collapsed.
 *   onOpenChange:  optional (newId) => void — fires when the user toggles
 */
export default function LeftPanelAccordion({ sections, defaultOpenId, onOpenChange }) {
  // `defaultOpenId === undefined` means "not loaded yet" (wait before opening anything).
  // `defaultOpenId === null` means "start collapsed".
  // `defaultOpenId === 'tasks'` (etc.) means "start with that section open".
  const [openId, setOpenId] = useState(() => (defaultOpenId === undefined ? null : defaultOpenId));

  // When the parent finishes loading its persisted preference, sync it in.
  useEffect(() => {
    if (defaultOpenId !== undefined) setOpenId(defaultOpenId);
  }, [defaultOpenId]);

  const handleToggle = (id) => {
    const newId = id === openId ? null : id;
    setOpenId(newId);
    if (onOpenChange) onOpenChange(newId);
    // Fire section's onOpen callback for lazy-loading
    if (newId) {
      const section = sections.find(s => s.id === newId);
      if (section && section.onOpen) section.onOpen();
    }
    // Fire-and-forget: any failure is silent, UI already updated optimistically.
    saveUiPreference({ left_panel_open_section: newId }).catch(() => {});
  };

  return (
    <div className="flex flex-col">
      {sections.map((section) => {
        const isOpen = openId === section.id;
        return (
          <div key={section.id}>
            <button
              onClick={() => handleToggle(section.id)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-800/40 transition-colors"
              style={{ borderTop: '1px solid #1f2937' }}
            >
              <div className="flex items-center gap-2">
                {isOpen
                  ? <ChevronDown className="h-3 w-3 text-gray-500" />
                  : <ChevronRight className="h-3 w-3 text-gray-500" />}
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                  {section.label}
                </span>
                {section.badge !== undefined && section.badge !== null && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                    style={{
                      background: section.badgeColor || '#1f2937',
                      color: section.warning ? '#f87171' : '#9ca3af',
                    }}
                  >
                    {section.badge}
                  </span>
                )}
                {section.warning && (
                  <span className="text-[10px] text-red-400 font-semibold">{section.warning}</span>
                )}
              </div>
            </button>
            {isOpen && <div>{section.render()}</div>}
          </div>
        );
      })}
    </div>
  );
}
